import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Checkbox,
  Button, CircularProgress, IconButton,
  Table, TableBody, TableRow, TableCell, TableContainer,
  FormControl, InputLabel, Select, MenuItem, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { api } from '../../../api';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

// ─── ImportBloodHistoryDialog ─────────────────────────────────────────────────
export default function ImportBloodHistoryDialog({
  open,
  onClose,
  contactId,
  bloodParams,      // param group object — used to filter by section slugs
  sectionSlug,      // e.g. 'blood-investigation-params-hormonal'
  sectionLabel,     // e.g. 'Hormonal'
  onImport,         // (selectedRecords: array) => void
  primaryColor,
}) {
  const [loading, setLoading]             = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [allVisits, setAllVisits]          = useState([]);
  const [selectedVisit, setSelectedVisit]  = useState('ALL_VISITS');
  const [records, setRecords]              = useState([]);
  const [selected, setSelected]            = useState({});
  const [importing, setImporting]          = useState(false);

  const memberId = bloodParams?.param_group_members?.find(m => m.slug === sectionSlug)?.id;

  // Step 1: fetch visits when dialog opens
  useEffect(() => {
    if (!open || !contactId) return;
    setLoading(true);
    setSelected({});
    setRecords([]);
    setSelectedVisit('ALL_VISITS');
    setAllVisits([]);

    api.contacts.getAllVisitsByContact(contactId)
      .then((res) => {
        const sorted = Array.isArray(res?.data)
          ? [...res.data].sort((a, b) => b.visit_no - a.visit_no)
          : [];
        setAllVisits(sorted);
      })
      .catch(() => setAllVisits([]))
      .finally(() => setLoading(false));
  }, [open, contactId]);

  // Step 2: fetch histories whenever selectedVisit changes (after visits loaded)
  useEffect(() => {
    if (!open || !contactId) return;
    setLoadingHistory(true);
    setSelected({});

    const params = selectedVisit !== 'ALL_VISITS' ? { visitNo: selectedVisit } : {};

    api.opd.getContactHistory(contactId, params)
      .then((res) => {
        const histories =
          Array.isArray(res?.data?.contact_histories) ? res.data.contact_histories :
          Array.isArray(res?.data) ? res.data :
          Array.isArray(res) ? res : [];

        const filtered = memberId
          ? histories.filter(h => h.irm_param_group_members_id === memberId)
          : histories;

        setRecords(filtered);
      })
      .catch(() => setRecords([]))
      .finally(() => setLoadingHistory(false));
  }, [open, contactId, selectedVisit, memberId]);

  const allSelected = records.length > 0 && records.every(h => selected[h.id]);

  const toggleAll = () => {
    const next = { ...selected };
    if (allSelected) {
      records.forEach(h => { next[h.id] = false; });
    } else {
      records.forEach(h => { next[h.id] = true; });
    }
    setSelected(next);
  };

  const toggleOne = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const selectedCount = records.filter(h => selected[h.id]).length;

  const handleImport = useCallback(async () => {
    const toImport = records.filter(h => selected[h.id]);
    if (toImport.length === 0) return;
    setImporting(true);
    try {
      await onImport(toImport);
      onClose();
    } finally {
      setImporting(false);
    }
  }, [records, selected, onImport, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh' } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, #005f75 100%)`,
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <DownloadIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                Import from Old History — {sectionLabel}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.75rem' }}>
                Select records to import into this procedure
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={8} gap={2}>
            <CircularProgress size={28} sx={{ color: primaryColor }} />
            <Typography color="text.secondary">Loading visits...</Typography>
          </Box>
        ) : (
          <>
            {/* ── Visit Dropdown ── */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
              <FormControl sx={{ minWidth: 280 }}>
                <InputLabel sx={{ color: primaryColor, '&.Mui-focused': { color: primaryColor } }}>
                  Select Visit
                </InputLabel>
                <Select
                  value={selectedVisit}
                  label="Select Visit"
                  onChange={(e) => setSelectedVisit(e.target.value)}
                  sx={{
                    height: 45,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                  }}
                >
                  <MenuItem value="ALL_VISITS">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AssignmentIcon sx={{ fontSize: '1rem', color: primaryColor }} />
                      <strong>All Visits</strong>
                    </Box>
                  </MenuItem>
                  {allVisits.map(v => (
                    <MenuItem key={v.id} value={v.visit_no}>
                      Visit #{String(v.visit_no).padStart(4, '0')}
                      {v.visit_date ? ` — ${fmt(v.visit_date)}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* ── Records Table ── */}
            <Box sx={{ px: 3, py: 2 }}>
              {loadingHistory ? (
                <Box display="flex" alignItems="center" justifyContent="center" py={6} gap={2}>
                  <CircularProgress size={22} sx={{ color: primaryColor }} />
                  <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>Loading history...</Typography>
                </Box>
              ) : records.length === 0 ? (
                <Box textAlign="center" py={6} sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px dashed #dee2e6' }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    No {sectionLabel} history found.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${primaryColor}30` }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ backgroundColor: `${primaryColor}12`, borderBottom: `2px solid ${primaryColor}40`, width: 40 }}>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={selectedCount > 0 && !allSelected}
                            onChange={toggleAll}
                            size="small"
                            sx={{ color: primaryColor, '&.Mui-checked': { color: primaryColor }, '&.MuiCheckbox-indeterminate': { color: primaryColor } }}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor}12`, borderBottom: `2px solid ${primaryColor}40`, fontWeight: 700, fontSize: '0.8rem', color: primaryColor }}>
                          Name of Test
                        </TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor}12`, borderBottom: `2px solid ${primaryColor}40`, fontWeight: 700, fontSize: '0.8rem', color: primaryColor }}>
                          Value
                        </TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor}12`, borderBottom: `2px solid ${primaryColor}40`, fontWeight: 700, fontSize: '0.8rem', color: primaryColor, whiteSpace: 'nowrap' }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ backgroundColor: `${primaryColor}12`, borderBottom: `2px solid ${primaryColor}40`, fontWeight: 700, fontSize: '0.8rem', color: primaryColor }}>
                          Remarks
                        </TableCell>
                      </TableRow>
                      {records.map((h, idx) => (
                        <TableRow
                          key={h.id}
                          hover
                          selected={Boolean(selected[h.id])}
                          onClick={() => toggleOne(h.id)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: selected[h.id] ? `${primaryColor}0a` : (idx % 2 === 0 ? 'white' : '#fafafa'),
                            '&.Mui-selected': { backgroundColor: `${primaryColor}12 !important` },
                            '&:hover': { backgroundColor: `${primaryColor}0d !important` },
                          }}
                        >
                          <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={Boolean(selected[h.id])}
                              onChange={() => toggleOne(h.id)}
                              size="small"
                              sx={{ color: primaryColor, '&.Mui-checked': { color: primaryColor } }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#212529' }}>
                            {h.param_group_member_option?.value ||
                             h.param_group_member_option?.option_value ||
                             h.name || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', maxWidth: 220 }}>
                            {/* eslint-disable-next-line react/no-danger */}
                            <span dangerouslySetInnerHTML={{ __html: h.value || '—' }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap', color: '#495057' }}>
                            {fmt(h.history_date)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', color: '#6c757d' }}>
                            {/* eslint-disable-next-line react/no-danger */}
                            <span dangerouslySetInnerHTML={{ __html: h.remarks || '—' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e9ecef', justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
          {selectedCount > 0 ? `${selectedCount} record${selectedCount > 1 ? 's' : ''} selected` : 'No records selected'}
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" onClick={onClose}
            sx={{ borderColor: '#dee2e6', color: '#6c757d', textTransform: 'none', fontWeight: 600, borderRadius: '7px', px: 2.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={selectedCount === 0 || importing}
            onClick={handleImport}
            sx={{
              backgroundColor: `${primaryColor} !important`,
              color: 'white !important',
              textTransform: 'none', fontWeight: 700,
              borderRadius: '7px', px: 2.5,
              '&.Mui-disabled': { opacity: 0.55 },
            }}
          >
            {importing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : `Import ${selectedCount > 0 ? `(${selectedCount})` : ''}`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

ImportBloodHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contactId: PropTypes.number,
  bloodParams: PropTypes.object,
  sectionSlug: PropTypes.string.isRequired,
  sectionLabel: PropTypes.string.isRequired,
  onImport: PropTypes.func.isRequired,
  primaryColor: PropTypes.string.isRequired,
};

ImportBloodHistoryDialog.defaultProps = {
  contactId: null,
  bloodParams: null,
};
